import styled from "styled-components";
import { countries, TCountryCode } from "countries-list";
import { ChangeEvent, useEffect, useId, useRef, useState } from "react";

interface CountryInputInterface {
    value?: TCountryCode,
    name?: string,
    onChange?: (e: ChangeEvent<HTMLSelectElement & HTMLInputElement>) => any,
    style?: Object,
    label?: string;
    disabled?: boolean;
    // Buscable (17/10/2026): un campo de texto donde se escribe el país, en vez de elegirlo de una
    // lista invisible detrás de la bandera. Lo usan los formularios del vendedor.
    buscable?: boolean;
}

// Nombre en español (Intl, lo traen todos los navegadores) y en inglés (countries-list): se busca por
// cualquiera de los dos. Si el navegador no tuviera Intl.DisplayNames, queda solo el inglés.
const enEspanol: ((code: string) => string | undefined) = (() => {
    try {
        const dn = new (Intl as any).DisplayNames(['es'], { type: 'region' })
        return (code: string) => { try { return dn.of(code) } catch { return undefined } }
    } catch { return () => undefined }
})()
const sinTildes = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
const PAISES = Object.entries(countries).map(([code, c]) => {
    const en = (c as { name: string }).name
    const es = enEspanol(code) || en
    return { code: code as TCountryCode, es, en, claves: [sinTildes(es), sinTildes(en)] }
}).sort((a, b) => a.es.localeCompare(b.es, 'es'))

// Código del país escrito completo (en español o inglés, sin importar mayúsculas ni tildes, o el código ISO), o undefined.
export function paisDe(texto: string): TCountryCode | undefined {
    const t = sinTildes(texto)
    if (!t) return undefined
    return PAISES.find(p => p.claves.includes(t) || p.code.toLowerCase() === t)?.code
}

// Países que calzan con lo escrito: primero los que empiezan así, después los que lo contienen.
export function buscarPaises(texto: string, max = 8) {
    const t = sinTildes(texto)
    if (!t) return PAISES.slice(0, max)
    const empiezan = PAISES.filter(p => p.claves.some(k => k.startsWith(t)))
    const contienen = PAISES.filter(p => !empiezan.includes(p) && p.claves.some(k => k.includes(t)))
    return [...empiezan, ...contienen].slice(0, max)
}

const nombreDe = (code?: TCountryCode) => (code && PAISES.find(p => p.code === code)?.es) || ''

// Lista propia (17/10/2026) en vez de <datalist>: el datalist no se desplegaba en el modal en desktop
// y solo conocía los nombres en inglés ("japón" no encontraba "Japan").
function CountrySearch({ value, onChange, name, style, label, disabled }: CountryInputInterface) {
    const lista = useId()
    const [texto, setTexto] = useState(nombreDe(value || 'CL'))
    const [abierto, setAbierto] = useState(false)
    const [resaltado, setResaltado] = useState(0)
    // Es el último campo del formulario: si abajo no cabe la lista, se abre hacia arriba.
    const campoRef = useRef<HTMLDivElement>(null)
    const [arriba, setArriba] = useState(false)
    const abrir = () => {
        const r = campoRef.current?.getBoundingClientRect()
        setArriba(!!r && window.innerHeight - r.bottom < 260 && r.top > 260)
        setAbierto(true)
    }
    useEffect(() => { if (value && paisDe(texto) !== value) setTexto(nombreDe(value)) }, [value]) // eslint-disable-line react-hooks/exhaustive-deps
    const elegido = paisDe(texto)
    const sugerencias = buscarPaises(texto)

    function elegir(code: TCountryCode) {
        setTexto(nombreDe(code))
        setAbierto(false)
        if (onChange) onChange({ target: { name: name || '', value: code, type: 'text' } } as any)
    }

    return (
        <Main style={{alignItems: 'stretch', ...style}}>
            {label && <label>{label}</label>}
            <Buscador>
                <img width={30} height={20} src={`/flags_3x2/${elegido || value || 'CL'}.svg`} alt="" />
                <div className="campo" ref={campoRef}>
                    <input name={name} value={texto} disabled={disabled} placeholder="Escribe el país"
                        role="combobox" aria-expanded={abierto} aria-controls={lista} aria-autocomplete="list"
                        aria-label={label || 'Nationality'} autoComplete="off"
                        onFocus={e => { abrir(); setResaltado(0); e.target.select() }}
                        onBlur={() => setAbierto(false)}
                        onKeyDown={e => {
                            if (e.key === 'ArrowDown') { e.preventDefault(); abrir(); setResaltado(i => Math.min(i + 1, sugerencias.length - 1)) }
                            else if (e.key === 'ArrowUp') { e.preventDefault(); setResaltado(i => Math.max(i - 1, 0)) }
                            else if (e.key === 'Enter' && abierto && sugerencias[resaltado]) { e.preventDefault(); elegir(sugerencias[resaltado].code) }
                            else if (e.key === 'Escape') setAbierto(false)
                        }}
                        onChange={e => {
                            setTexto(e.target.value)
                            abrir()
                            setResaltado(0)
                            const code = paisDe(e.target.value)
                            if (code && onChange) onChange({ target: { name: name || '', value: code, type: 'text' } } as any)
                        }} />
                    {
                        abierto && sugerencias.length > 0 &&
                        <ul id={lista} role="listbox" className={arriba ? 'arriba' : ''}>
                            {sugerencias.map((p, i) => (
                                <li key={p.code} role="option" aria-selected={i === resaltado} className={i === resaltado ? 'resaltado' : ''}
                                    // mousedown (no click): así elige antes de que el campo pierda el foco y cierre la lista.
                                    onMouseDown={e => { e.preventDefault(); elegir(p.code) }}
                                    onMouseEnter={() => setResaltado(i)}>
                                    <img width={24} height={16} src={`/flags_3x2/${p.code}.svg`} alt="" />
                                    {p.es}
                                </li>
                            ))}
                        </ul>
                    }
                </div>
            </Buscador>
            {texto && !elegido && !abierto && <small>Elige un país de la lista.</small>}
            {texto && !abierto && sugerencias.length === 0 && <small>No hay países con ese nombre.</small>}
        </Main>
    )
}

export default function CountryInput({value, onChange, name, style, label, disabled, buscable}: CountryInputInterface) {
    const defaultCountry = 'CL'
    if (buscable) return <CountrySearch value={value} onChange={onChange} name={name} style={style} label={label} disabled={disabled} />
    return (
        <Main>
            {
                label &&
                <label>{label}</label>
            }
            <Dropdown style={style}>
                <select name={name} defaultValue={defaultCountry} onChange={onChange} disabled={disabled}>
                  {Object.keys(countries).map((country, index) => {
                      return (
                          <option key={index} value={country}>
                        {countries[country as keyof typeof countries].name}
                      </option>
                    );
                })}
                </select>
                <img width={40} height={20} src={`/flags_3x2/${value || defaultCountry}.svg`} alt="" />
            </Dropdown>
        </Main>
    )
}

const Main = styled.div`
display: flex;
flex-direction: column;
justify-content: center;
align-items: center;
width: fit-content;
gap: 5px;
`

const Buscador = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  > img { height: 24px; width: auto; }
  .campo { position: relative; flex: 1; }
  input { width: 100%; min-width: 180px; box-sizing: border-box; border: 1px solid #c9c9c9; border-radius: 8px; padding: 8px; font-size: 16px; }
  ul {
    position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 3000;
    margin: 0; padding: 4px 0; list-style: none; max-height: 240px; overflow-y: auto;
    background: #fff; border: 1px solid #c9c9c9; border-radius: 8px; box-shadow: 0 6px 16px rgba(0,0,0,.15);
  }
  ul.arriba { top: auto; bottom: calc(100% + 4px); }
  li { display: flex; align-items: center; gap: 8px; padding: 10px 12px; font-size: 14px; cursor: pointer; }
  li.resaltado { background: #eef2f7; }
`

const Dropdown = styled.div`
  padding: 5px;
  position: relative;
  /* margin-left: 5px; */
  select {
    position: absolute;
    height: 100%;
    width: 100%;
    opacity: 0;
  }
  img {
    height: 30px;
    padding: 5px;
    background: #f7f7f7;
  }
`;